import React, { useEffect, useState, useContext } from 'react';
import Popper from '@material-ui/core/Popper';
import Fade from '@material-ui/core/Fade';
import { Paper, MenuItem, ListSubheader, IconButton } from '@material-ui/core';
import { useDispatch, useSelector } from 'react-redux';
import ArrowDropDownIcon from '@material-ui/icons/ArrowDropDown';
import { isCalFamily } from './FilterCondition';
import {
	DC_TMPT_LIST,
	GREGORIAN_CALENDAR,
	MTS_COLUMN_TITLE,
	BASIC,
	MTS_TEMPLATE_LIST_BASIC,
	MTS_TEMPLATE_LIST_CHARTER,
	WALMART_CALENDAR,
	MULTI_TIME_SELECTION,
	PURCHASE_ORDER_LIST,
	GENERAL_CANNED_REPORT_LIST
} from 'constants/constant';
import { UserPortalContext } from 'context/appcontext';
import { resetPivotOptions } from '../Preview.actions';
import { FEATURES } from 'helper/basicFeatureFlag';
import { getTmplName, getSavedCustomDatasetWithAttributes, isCustomDataset } from 'helper/utils';

function ShowDateElements({ mtsSubAttr, cbHandleDateFilterChange }) {
	const dispatch = useDispatch();
	const [anchorEl, setAnchorEl] = useState(null);
	const [open, setOpen] = useState(false);
	const [timeElements, setTimeElements] = useState([]);
	const [timeFamilySel, setTimeFamily] = useState(undefined);
	const portalUserContext = useContext(UserPortalContext);
	const { customerType } = portalUserContext;

	const selectedTemplate = useSelector((state) => state.templateReducer.selectedTemplate);
	const templateData = useSelector((state) => state.templateReducer.templateData);
	const cannedReportData = useSelector((state) => state.templateReducer.cannedReportData);
	const savedCustomDatasets = useSelector((state) => state.templateReducer.savedCustomDatasets);
	const customDatasetDetails = useSelector((state) => state.templateReducer.customDatasetDetails);
	const customAttribute = useSelector((state) => state.templateReducer.customAttribute);
	const { selectedList, pivotOpts } = useSelector((state) => state.previewReducer);
	// eslint-disable-next-line prettier/prettier
	const { DISABLE_GREGORIAN_CALENDAR: disableGregorianCalendar } =
		FEATURES[customerType].PREVIEW_PAGE;

	const getTimeElements = (result) => {
		/*
			Removing 'Walmart Week' option from PO time elements options as filter 
			cannot be applied for the same 
		*/
		if (PURCHASE_ORDER_LIST.includes(Number(selectedTemplate))) {
			const newTimeElements = result[0].time.map((item) => ({
				...item,
				subAttributes: item.subAttributes.filter((i) => i.businessName !== 'Walmart Week')
			}));
			return setTimeElements(newTimeElements);
		}

		setTimeElements(result[0].time);
	};

	useEffect(() => {
		const customDatasetsWithTime = getSavedCustomDatasetWithAttributes(
			savedCustomDatasets,
			customDatasetDetails,
			customAttribute
		);
		const allData = [...cannedReportData, ...templateData, ...customDatasetsWithTime];
		const result = allData.filter((item) => {
			if (isCustomDataset(selectedTemplate)) {
				return item.templateId === selectedTemplate;
			} else {
				return item.templateName === getTmplName(selectedTemplate);
			}
		});
		getTimeElements(result);
		const timeFilter = selectedList.find((el) => isCalFamily(el));
		if (timeFilter !== undefined) {
			setTimeFamily(timeFilter);
		}
	}, []);

	function handleShowDateEl(e) {
		e.stopPropagation();
		setOpen(!open);
		setAnchorEl(e.currentTarget);
	}

	function handleDateElementSelection(event, parent, attr) {
		event.stopPropagation();
		setOpen(!open);
		cbHandleDateFilterChange({ parent, attr });
		// Reset Pivot if user has applied any
		if (pivotOpts?.pivotColumn) {
			dispatch(resetPivotOptions());
		}
	}

	function doDisable(parent, attr) {
		// If it is MTS and an attribute other than MTS has been selected, then disable MTS
		if (attr?.localName === MULTI_TIME_SELECTION) {
			// timeFamilySel === undefined would mean that the user has selected a column which is not MTS
			if (timeFamilySel !== undefined) {
				return true;
			}
		}
		// Disable Gregorian & WMT Year option for Basic
		if (disableGregorianCalendar && parent.businessName === GREGORIAN_CALENDAR) {
			return true;
		}

		if (timeFamilySel === undefined) {
			return false;
		}

		// Need to add all week specific templates because only one option allowed to select.
		const WK_SPEC_TMPT = [...DC_TMPT_LIST, ...GENERAL_CANNED_REPORT_LIST];
		if (WK_SPEC_TMPT.includes(Number(selectedTemplate))) {
			return attr.businessName !== timeFamilySel.businessName;
		}

		return parent.businessName !== timeFamilySel.parent;
	}

	/** If there is only one option available then don't show the dropdown */
	if (timeElements.length > 0) {
		let options = 0;
		timeElements.forEach((el) => {
			if (el.subAttributes.length > 0) {
				options = options + el.subAttributes.length;
			}
		});
		if (options < 2) return null;
	}

	const mtsParent = timeElements?.find((el) => el?.businessName === WALMART_CALENDAR);
	const MTSEnabledTemplates =
		customerType === BASIC ? MTS_TEMPLATE_LIST_BASIC : MTS_TEMPLATE_LIST_CHARTER;
	return (
		<>
			<IconButton size="small" onClick={handleShowDateEl} id="show-date-menu">
				<ArrowDropDownIcon sx={{ fontSize: 24 }} />
			</IconButton>
			<Popper open={open} anchorEl={anchorEl} transition style={{ zIndex: 9999 }}>
				{({ TransitionProps }) => (
					<Fade {...TransitionProps} timeout={350}>
						<Paper>
							{MTSEnabledTemplates.includes(Number(selectedTemplate)) && (
								<MenuItem
									id={`${mtsParent.businessName}-${mtsSubAttr?.localName}`}
									key={`${mtsParent.businessName}-${mtsSubAttr?.localName}`}
									onClick={(e) =>
										handleDateElementSelection(e, mtsParent?.businessName, mtsSubAttr)
									}
									disabled={doDisable(mtsParent, mtsSubAttr)}>
									{MTS_COLUMN_TITLE}
								</MenuItem>
							)}
							{timeElements.map((el, index) => {
								return (
									<>
										<ListSubheader key={`Abc-${index}`}>{el.businessName}</ListSubheader>
										{el.subAttributes.map((attr, idx) => {
											return (
												<MenuItem
													id={`${el.businessName}-${idx}`}
													key={`${el.businessName}-${idx}`}
													onClick={(e) => handleDateElementSelection(e, el.businessName, attr)}
													disabled={doDisable(el, attr)}>
													{attr.businessName}
												</MenuItem>
											);
										})}
									</>
								);
							})}
						</Paper>
					</Fade>
				)}
			</Popper>
		</>
	);
}

export default ShowDateElements;
